import { useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { useLocales } from '@/hooks/use-locales';
import {
  I18N_SURFACES,
  SURFACE_LABEL,
  SURFACE_LABEL_KEY,
  useI18nStringsBulk,
  useUpsertI18nString,
  type I18nSurface,
} from '@/hooks/use-i18n-strings';
import { useT } from '@/i18n/use-t';
import { cn } from '@/lib/utils';

function extractError(err: unknown): string | null {
  if (isAxiosError(err)) {
    const body = err.response?.data as { message?: string; code?: string } | undefined;
    if (body?.message) return body.code ? `${body.code}: ${body.message}` : body.message;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return null;
}

export function I18nStringsPage() {
  const t = useT('admin_ui');
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSurface = searchParams.get('surface');
  const surface: I18nSurface = I18N_SURFACES.some((candidate) => candidate === requestedSurface)
    ? (requestedSurface as I18nSurface)
    : 'admin_ui';
  const [filter, setFilter] = useState('');
  const [newKey, setNewKey] = useState('');
  const [newKeyValues, setNewKeyValues] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);

  const { data: locales } = useLocales();
  const enabledLocales = useMemo(
    () => (locales ?? []).filter((l) => l.enabled).map((l) => l.code),
    [locales],
  );
  const stringsByLocale = useI18nStringsBulk(surface, enabledLocales);
  const upsert = useUpsertI18nString();

  // Merge all locales' string sets into a single { key → { locale → value } } map.
  const keys = useMemo(() => {
    const set = new Set<string>();
    stringsByLocale.forEach((q) => {
      if (q.data === undefined) return;
      for (const k of Object.keys(q.data)) set.add(k);
    });
    const all = Array.from(set).sort();
    if (filter.trim() === '') return all;
    const f = filter.toLowerCase();
    return all.filter((k) => k.toLowerCase().includes(f));
    // Depend on each query's `dataUpdatedAt` (a timestamp that changes on
    // every (re)fetch) rather than `q.data` — objects stringify to
    // "[object Object]", so a newly-added key after an invalidate would
    // leave the joined string unchanged and the key list stale.
  }, [stringsByLocale.map((q) => q.dataUpdatedAt).join('|'), filter]);

  function valueFor(key: string, locale: string): string {
    const idx = enabledLocales.indexOf(locale);
    if (idx < 0) return '';
    return stringsByLocale[idx]?.data?.[key] ?? '';
  }

  async function commitEdit(localeCode: string, key: string, value: string) {
    setBanner(null);
    try {
      await upsert.mutateAsync({ surface, localeCode, key, value });
    } catch (err) {
      setBanner(extractError(err) ?? t('i18nEditor.saveFailed', 'Save failed.'));
    }
  }

  async function addNewKey() {
    setBanner(null);
    if (newKey.trim() === '') return;
    try {
      // Write a row for every locale that has a non-empty value.
      // Locales left blank don't get an entry — first edit lands later.
      const writes = enabledLocales
        .filter((loc) => (newKeyValues[loc] ?? '').trim() !== '')
        .map((loc) =>
          upsert.mutateAsync({
            surface,
            localeCode: loc,
            key: newKey.trim(),
            value: newKeyValues[loc]!.trim(),
          }),
        );
      if (writes.length === 0) {
        setBanner(t('i18nEditor.needValue', 'Provide a value for at least one locale.'));
        return;
      }
      await Promise.all(writes);
      setNewKey('');
      setNewKeyValues({});
    } catch (err) {
      setBanner(extractError(err) ?? t('i18nEditor.saveFailed', 'Save failed.'));
    }
  }

  return (
    <div className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('i18nEditor.title', 'i18n strings')}</h1>
          <p className="text-sm text-muted-foreground">
            {t(
              'i18nEditor.description',
              'Translations per surface (admin UI / POS / SCO / attendant / shared / receipt-labels). Edits write through to the live DB; terminal-bound surfaces ship in the next sync delta.',
            )}
          </p>
        </div>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label htmlFor="surface">{t('i18nEditor.surface', 'Surface')}</Label>
          <select
            id="surface"
            className="mt-1 w-44 rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={surface}
            onChange={(e) => {
              const next = e.target.value as I18nSurface;
              setSearchParams(
                (current) => {
                  const params = new URLSearchParams(current);
                  if (next === 'admin_ui') params.delete('surface');
                  else params.set('surface', next);
                  return params;
                },
                { replace: true },
              );
            }}
          >
            {I18N_SURFACES.map((s) => (
              <option key={s} value={s}>
                {t(SURFACE_LABEL_KEY[s], SURFACE_LABEL[s])}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 max-w-md">
          <Label htmlFor="filter">{t('i18nEditor.filter', 'Filter keys')}</Label>
          <Input
            id="filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t('i18nEditor.filterPlaceholder', 'e.g. nav. or button.')}
            className="mt-1"
          />
        </div>
      </div>

      {banner !== null && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {banner}
        </p>
      )}

      <div className="rounded-md border border-border bg-card">
        <Table>
          <THead>
            <TR>
              <TH className="w-72">{t('i18nEditor.key', 'Key')}</TH>
              {enabledLocales.map((loc) => (
                <TH key={loc} className="font-mono text-xs">
                  {loc}
                </TH>
              ))}
            </TR>
          </THead>
          <TBody>
            {keys.length === 0 ? (
              <TR>
                <TD
                  colSpan={1 + enabledLocales.length}
                  className="text-center text-muted-foreground"
                >
                  {t('i18nEditor.empty', 'No keys match. Use the form below to add the first one.')}
                </TD>
              </TR>
            ) : (
              keys.map((k) => (
                <TR key={k}>
                  <TD className="font-mono text-xs">{k}</TD>
                  {enabledLocales.map((loc) => (
                    <TD key={loc} className="p-1">
                      <EditableCell
                        initial={valueFor(k, loc)}
                        onCommit={(next) => commitEdit(loc, k, next)}
                      />
                    </TD>
                  ))}
                </TR>
              ))
            )}
          </TBody>
        </Table>
      </div>

      <section className="rounded-md border border-border bg-card p-4">
        <header className="mb-3 text-sm font-medium">
          {t('i18nEditor.addKey', 'Add new key')}
        </header>
        <div className="grid grid-cols-[16rem_1fr] gap-3 items-start">
          <Input
            placeholder="namespace.key.path"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
          />
          <div
            className="grid gap-2"
            style={{
              gridTemplateColumns: `repeat(${Math.max(1, enabledLocales.length)}, minmax(0, 1fr))`,
            }}
          >
            {enabledLocales.map((loc) => (
              <Input
                key={loc}
                placeholder={loc}
                value={newKeyValues[loc] ?? ''}
                onChange={(e) => setNewKeyValues((prev) => ({ ...prev, [loc]: e.target.value }))}
              />
            ))}
          </div>
        </div>
        <div className="mt-3">
          <Button onClick={addNewKey} disabled={upsert.isPending || newKey.trim() === ''}>
            {upsert.isPending
              ? t('common.saving', 'Saving…')
              : t('i18nEditor.addKeySubmit', 'Add key')}
          </Button>
        </div>
      </section>
    </div>
  );
}

/** Exported for `i18n-strings.test.tsx`; rendered only by the table above. */
export function EditableCell({
  initial,
  onCommit,
}: {
  initial: string;
  onCommit: (next: string) => void;
}) {
  const [v, setV] = useState(initial);
  // RL-362. This state is seeded once, on the cell's first render — which is
  // while the catalogue query is still loading, so `initial` is ''. Without the
  // sync below the cell keeps rendering '' after the values arrive, and every
  // translation in the table reads as empty. The guard that used to stand here
  // could never fire: it asked for `initial !== v && !dirty` while `dirty` is
  // defined as `v !== initial`, which is the same condition negated.
  //
  // Worse than a display bug: blur commits when `v !== initial`, so tabbing
  // through an "empty" table wrote '' over real translations. React's own
  // pattern for adjusting state to a changed prop is this render-phase compare,
  // not an effect — an effect would paint the stale value for a frame first.
  const [upstream, setUpstream] = useState(initial);
  if (initial !== upstream) {
    setUpstream(initial);
    setV(initial);
  }
  const dirty = v !== initial;
  return (
    <Input
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => {
        if (v !== initial) onCommit(v);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          if (v !== initial) onCommit(v);
          (e.currentTarget as HTMLInputElement).blur();
        }
        if (e.key === 'Escape') {
          setV(initial);
          (e.currentTarget as HTMLInputElement).blur();
        }
      }}
      className={cn(
        'border-transparent bg-transparent text-xs hover:border-input focus:border-input',
        dirty && 'border-primary/40',
      )}
    />
  );
}
